/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Icon, SemanticICONS, Table } from 'semantic-ui-react';

import {
  getListReport,
  getReportTotalHoursAllSubordinates,
  getReportTotalHoursEmployees,
} from '../../../../services/report';
import { IReportFilterParamsProps } from '../../../../types/filters';
import { Report } from '../../../../types/report';
import { SubTable } from '../SubTable';

interface IProps {
  index: number;
  report: Report;
  query: IReportFilterParamsProps;
}

export function TableRow({ index, query, report }: IProps) {
  const [isExpandedRow, setIsExpandedRow] = useState(false);
  const [totalGeneralArea, setTotalGeneralArea] = useState<number>();
  const [totalImmediate, setTotalImmediate] = useState<number>();

  function toggleExpandedRow() {
    setIsExpandedRow((prev) => !prev);
  }

  const { data: reports, isLoading: isLoadingReports } = useQuery(
    [report, query],
    async () =>
      getListReport({
        mandatoryId: report.mandatory.id,
        sbuId: report.mandatory.sbu.id,
        userId: report.employee.za,
      }),
    {
      enabled: !!report && !!query && isExpandedRow,
    }
  );

  const handleTotalImmediate = useMutation({
    mutationFn: async () =>
      getReportTotalHoursEmployees({
        mandatoryId: report.mandatory.id,
        sbuId: report.mandatory.sbu.id,
        managerUserId: report.employee.za,
      }),
    onSuccess: (data) => {
      setTotalImmediate(data);
    },
    onError: () => {
      setTotalImmediate(report.totalPerArea);
    },
  });

  const handleTotalGeneralArea = useMutation({
    mutationFn: async () =>
      getReportTotalHoursAllSubordinates({
        mandatoryId: report.mandatory.id,
        sbuId: report.mandatory.sbu.id,
        managerUserId: report.employee.za,
      }),
    onSuccess: (data) => {
      setTotalGeneralArea(data);
    },
    onError: () => {
      setTotalGeneralArea(undefined);
    },
  });

  useEffect(() => {
    const init = async () => {
      const promises = [
        handleTotalImmediate.mutateAsync(),
        handleTotalGeneralArea.mutateAsync(),
      ];

      Promise.all(promises);
    };

    init();
  }, []);

  const iconName = useMemo((): SemanticICONS => {
    if (isLoadingReports) {
      return 'sync';
    }

    if (reports?.content.length === 0) {
      return 'dont';
    }

    return `chevron ${isExpandedRow ? 'down' : 'right'}`;
  }, [isExpandedRow, isLoadingReports, reports?.content.length]);

  return (
    <>
      <Table.Row
        key={`${report.bossId} - ${report.employee.id}`}
        className="tr noRowspan"
        style={{ position: 'relative' }}
      >
        <Table.Cell
          className="td collapse_btn"
          content={
            <Icon
              name={iconName}
              link
              circular
              basic
              onClick={toggleExpandedRow}
              loading={isLoadingReports}
            />
          }
        />

        <Table.Cell className="td table-idTr" textAlign="center">
          {index + 1}
        </Table.Cell>

        <Table.Cell className="td" textAlign="center">
          {report.mandatory?.year.split('-')[0]}
        </Table.Cell>

        <Table.Cell className="td" textAlign="center">
          {report.mandatory.sbu?.country}
        </Table.Cell>

        <Table.Cell
          className="td report_employee_name"
          content={
            <Link
              className="linkUnderlined"
              to={`/development/${report.mandatory.id}/${report.mandatory.sbu.id}`}
              state={report.employee?.za}
            >
              {report.employee?.name}
            </Link>
          }
        />

        {report.values?.map(({ trainingType, value }) => (
          <Table.Cell
            key={trainingType}
            className="td"
            textAlign="center"
            content={value}
          />
        ))}

        <Table.Cell className="td" textAlign="center">
          {report.total}
        </Table.Cell>

        <Table.Cell className="td" textAlign="center">
          {totalImmediate ?? (
            <Icon
              name={`${handleTotalImmediate.isLoading ? 'sync' : 'search'}`}
              link
              circular
              basic
              onClick={() => handleTotalImmediate.mutateAsync()}
              loading={handleTotalImmediate.isLoading}
            />
          )}
        </Table.Cell>

        <Table.Cell className="td" textAlign="center">
          {totalGeneralArea ?? (
            <Icon
              name={`${handleTotalGeneralArea.isLoading ? 'sync' : 'search'}`}
              link
              circular
              basic
              onClick={() => handleTotalGeneralArea.mutateAsync()}
              loading={handleTotalGeneralArea.isLoading}
            />
          )}
        </Table.Cell>
      </Table.Row>

      {!!reports?.content.length && isExpandedRow && !isLoadingReports && (
        <SubTable query={query} reports={reports?.content} />
      )}
    </>
  );
}
